import { useMemo } from "react";
import {
  CircleCheck,
  LayoutGrid,
  PanelsTopLeft,
  ShoppingCart,
} from "lucide-react";
import type { Order } from "../../../api/SolarCloudApi/models/Order";
import type { TrackerSummary } from "../../../queries/tracker/useTrackerSummary";
import { Button } from "../../../components/ui/Button";
import { Card, CardContent } from "../../../components/ui/Card";
import { getOwnershipSummary } from "./bottomDashboardCards";

type OwnershipSummaryCardProps = {
  activeWatts?: number;
  pendingCapacityWatts?: number;
  orders?: Order[];
  trackerSummary?: TrackerSummary;
  isUserLoading: boolean;
  isPowerGenerationLoading: boolean;
  isOrdersLoading: boolean;
  isTrackerSummaryPending: boolean;
  userError?: Error | null;
  powerGenerationError?: Error | null;
  ordersError?: Error | null;
  trackerSummaryError?: Error | null;
  onRetry?: () => void;
};

export default function OwnershipSummaryCard({
  activeWatts,
  pendingCapacityWatts,
  orders,
  trackerSummary,
  isUserLoading,
  isPowerGenerationLoading,
  isOrdersLoading,
  isTrackerSummaryPending,
  userError,
  powerGenerationError,
  ordersError,
  trackerSummaryError,
  onRetry,
}: OwnershipSummaryCardProps) {
  const summary = useMemo(
    () =>
      getOwnershipSummary(
        activeWatts,
        orders,
        trackerSummary,
        pendingCapacityWatts
      ),
    [activeWatts, orders, pendingCapacityWatts, trackerSummary]
  );
  const hasMissingSummaryError =
    (userError && pendingCapacityWatts === undefined) ||
    (powerGenerationError && activeWatts === undefined) ||
    (ordersError && orders === undefined) ||
    (trackerSummaryError && trackerSummary === undefined);
  const hasStaleSummaryError =
    (userError && pendingCapacityWatts !== undefined) ||
    (powerGenerationError && activeWatts !== undefined) ||
    (ordersError && orders !== undefined) ||
    (trackerSummaryError && trackerSummary !== undefined);
  const isLoading =
    isUserLoading || isPowerGenerationLoading || isOrdersLoading;
  const hasNoSummaryData =
    activeWatts === undefined &&
    orders === undefined &&
    pendingCapacityWatts === undefined &&
    !isLoading &&
    !userError &&
    !powerGenerationError &&
    !ordersError &&
    !trackerSummaryError;
  let statusLabel = "—";
  let statusDescription = "—";

  if (summary.status === "active") {
    statusLabel = "Active";
    statusDescription = "Generating savings";
  } else if (summary.status === "inProgress") {
    statusLabel = "In progress";
    statusDescription = "Your system is being prepared";
  } else if (summary.status === "awaitingActivation") {
    statusLabel = "Awaiting activation";
    statusDescription = "System not yet generating";
  } else if (summary.status === "pending") {
    statusLabel = "Pending";
    statusDescription = "Awaiting setup";
  } else if (isTrackerSummaryPending) {
    statusLabel = "Checking";
    statusDescription = "Checking status";
  } else if (userError || powerGenerationError || ordersError || trackerSummaryError) {
    statusLabel = "Unavailable";
    statusDescription = "Status unavailable";
  }
  const metrics = [
    {
      label: "System size",
      value: formatKilowatts(summary.systemSizeKw),
      icon: PanelsTopLeft,
      valueClassName: "text-[#E9A837]",
    },
    {
      label: "SolarCloud panels",
      value: formatCount(summary.panelCount),
      icon: LayoutGrid,
      valueClassName: "text-[#E9A837]",
    },
    {
      label: "Total purchases",
      value: formatCount(summary.purchaseCount),
      icon: ShoppingCart,
      valueClassName: "text-[#D0D0D0]",
    },
  ];

  return (
    <Card
      aria-busy={isLoading || isTrackerSummaryPending}
      className="h-full rounded-[20px] border-[#232836] bg-[#12151C] py-0 text-[#D0D0D0] shadow-none"
    >
      <CardContent className="flex h-full flex-col p-2.5">
        <h2 className="text-sm font-semibold leading-5 text-[#D0D0D0]">
          Your SolarCloud Ownership Summary
        </h2>

        {isLoading && (
          <p className="mt-1 text-[10px] text-[#9CA3AF]" role="status">
            Loading system details…
          </p>
        )}
        {hasNoSummaryData && (
          <p className="mt-1 text-[10px] text-[#9CA3AF]" role="status">
            System details are unavailable.
          </p>
        )}
        {hasMissingSummaryError && (
          <div className="mt-1 flex items-center justify-between gap-3" role="alert">
            <p className="text-[10px] text-amber-300">
              Some system details could not be loaded.
            </p>
            {onRetry && (
              <Button
                className="h-6 rounded-lg border border-[#2F3648] px-2 text-[10px] text-[#D0D0D0] hover:bg-[#181C25]"
                onClick={onRetry}
                type="button"
                variant="outline"
              >
                Retry
              </Button>
            )}
          </div>
        )}
        {hasStaleSummaryError && (
          <p className="mt-1 text-[10px] text-amber-300" role="status">
            Some values may be from the last successful refresh.
          </p>
        )}

        <div className="mt-auto grid grid-cols-2 gap-x-1.5 gap-y-2.5 pt-2">
          {metrics.map(({ label, value, icon: Icon, valueClassName }) => (
            <div
              className="flex min-w-0 items-center gap-2 rounded-[11px] border border-[#1C2B39] bg-[#101923] p-1.5"
              key={label}
            >
              <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-[#29240F] text-[#E9A837]">
                <Icon aria-hidden="true" className="size-5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-[10px] leading-3 text-[#9CA3AF]">
                  {label}
                </p>
                <p className={`mt-0.5 truncate text-sm font-bold leading-4 ${valueClassName}`}>
                  {value}
                </p>
              </div>
            </div>
          ))}
          <div
            aria-label={`System status: ${statusLabel}. ${statusDescription}`}
            className="flex min-w-0 items-center gap-2 rounded-[11px] border border-[#1C2B39] bg-[#101923] p-1.5"
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-[#10251B] text-[#39D514]">
              <CircleCheck aria-hidden="true" className="size-6" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-4 text-[#39D514]">
                {statusLabel}
              </p>
              <p className="mt-0.5 truncate text-[10px] leading-3 text-[#9CA3AF]">
                {statusDescription}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function formatKilowatts(value: number | undefined): string {
  if (value === undefined) return "—";

  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} kW`;
}

function formatCount(value: number | undefined): string {
  return value === undefined ? "—" : new Intl.NumberFormat().format(value);
}
